import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Users, Plus, MapPin } from "lucide-react";

function CrewCard({ crew }) {
    return (
        <Link
            to={`/crews/${crew.slug}`}
            className="group bg-zinc-900/50 border border-zinc-800/60 rounded-sm overflow-hidden hover:border-zinc-600/60 hover:-translate-y-1 transition-all duration-300"
            data-testid={`crew-card-${crew.slug}`}
        >
            <div className="relative h-44 bg-zinc-900 overflow-hidden">
                {crew.cover_url && (
                    <img
                        src={crew.cover_url}
                        alt={crew.name}
                        className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity"
                    />
                )}
                <div className="absolute top-3 left-3">
                    <span className="overline bg-black/70 backdrop-blur px-2 py-1 rounded-sm">
                        {crew.focus === "cars" ? "Cars" : crew.focus === "bikes" ? "Bikes" : "Mixed"}
                    </span>
                </div>
            </div>
            <div className="p-5">
                <h3 className="font-display text-xl font-bold tracking-tight text-white">
                    {crew.name}
                </h3>
                <p className="font-body text-sm text-zinc-400 mt-1 line-clamp-2">
                    {crew.description || "—"}
                </p>
                <div className="flex items-center gap-3 mt-3 text-xs text-zinc-500 font-body">
                    <span className="inline-flex items-center gap-1">
                        <Users size={12} /> {(crew.members || []).length}
                    </span>
                    {crew.region && (
                        <span className="inline-flex items-center gap-1">
                            <MapPin size={12} /> {crew.region}
                        </span>
                    )}
                </div>
            </div>
        </Link>
    );
}

function CreateCrewModal({ onClose, onCreated }) {
    const [form, setForm] = useState({
        name: "",
        description: "",
        cover_url: "",
        region: "",
        focus: "both",
    });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");

    const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setError("");
        try {
            const { data } = await api.post("/crews", form);
            onCreated(data);
            onClose();
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
            onClick={onClose}
            data-testid="create-crew-modal"
        >
            <form
                onClick={(e) => e.stopPropagation()}
                onSubmit={submit}
                className="bg-[var(--crewz-bg)] border border-zinc-800 rounded-sm w-full max-w-lg p-6"
            >
                <p className="overline">New crew</p>
                <h3 className="font-display text-2xl font-bold tracking-tight text-white mt-2">
                    Start a Crew
                </h3>
                <div className="mt-5 space-y-3">
                    <input
                        value={form.name}
                        onChange={update("name")}
                        placeholder="Crew name"
                        required
                        className="w-full bg-zinc-900 border border-zinc-800 focus:border-red-600 rounded-sm px-3 py-2 text-sm font-body text-white outline-none"
                        data-testid="crew-name-input"
                    />
                    <textarea
                        value={form.description}
                        onChange={update("description")}
                        placeholder="What's this crew about?"
                        rows={3}
                        className="w-full bg-zinc-900 border border-zinc-800 focus:border-red-600 rounded-sm px-3 py-2 text-sm font-body text-white outline-none resize-none"
                        data-testid="crew-description-input"
                    />
                    <input
                        value={form.cover_url}
                        onChange={update("cover_url")}
                        placeholder="Cover image URL (optional)"
                        className="w-full bg-zinc-900 border border-zinc-800 focus:border-red-600 rounded-sm px-3 py-2 text-sm font-body text-white outline-none"
                        data-testid="crew-cover-input"
                    />
                    <div className="grid grid-cols-2 gap-3">
                        <input
                            value={form.region}
                            onChange={update("region")}
                            placeholder="Region"
                            className="bg-zinc-900 border border-zinc-800 focus:border-red-600 rounded-sm px-3 py-2 text-sm font-body text-white outline-none"
                            data-testid="crew-region-input"
                        />
                        <select
                            value={form.focus}
                            onChange={update("focus")}
                            className="bg-zinc-900 border border-zinc-800 focus:border-red-600 rounded-sm px-3 py-2 text-sm font-body text-white outline-none"
                            data-testid="crew-focus-select"
                        >
                            <option value="both">Cars & Bikes</option>
                            <option value="cars">Cars only</option>
                            <option value="bikes">Bikes only</option>
                        </select>
                    </div>
                </div>
                {error && <p className="text-xs text-red-500 font-body mt-3">{error}</p>}
                <div className="flex items-center justify-end gap-2 mt-5">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-sm text-zinc-400 hover:text-white"
                        data-testid="crew-cancel-btn"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={submitting}
                        className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-sm font-semibold tracking-wide text-sm transition-colors disabled:opacity-50"
                        data-testid="crew-create-submit-btn"
                    >
                        {submitting ? "Creating…" : "Create Crew"}
                    </button>
                </div>
            </form>
        </div>
    );
}

export default function Crews() {
    const { user } = useAuth();
    const [crews, setCrews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreate, setShowCreate] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get("/crews");
            setCrews(data);
        } catch (_e) {
            /* ignore */
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    return (
        <div className="min-h-screen bg-[var(--crewz-bg)]" data-testid="crews-page">
            <Navbar />
            <main className="max-w-6xl mx-auto px-6 py-10">
                <div className="flex items-end justify-between mb-8">
                    <div>
                        <p className="overline">Find your people</p>
                        <h1 className="font-display text-4xl font-black tracking-tighter text-white mt-2">
                            Crews
                        </h1>
                    </div>
                    {user && (
                        <button
                            onClick={() => setShowCreate(true)}
                            className="inline-flex items-center gap-1.5 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-sm font-semibold text-sm tracking-wide transition-colors"
                            data-testid="open-create-crew-btn"
                        >
                            <Plus size={14} /> New Crew
                        </button>
                    )}
                </div>

                {loading ? (
                    <p className="text-zinc-500 font-body" data-testid="crews-loading">
                        Loading crews…
                    </p>
                ) : crews.length === 0 ? (
                    <p className="text-zinc-500 font-body" data-testid="crews-empty">
                        No crews yet. Be the first to start one.
                    </p>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {crews.map((c) => (
                            <CrewCard key={c.id} crew={c} />
                        ))}
                    </div>
                )}
            </main>
            <Footer />
            {showCreate && (
                <CreateCrewModal
                    onClose={() => setShowCreate(false)}
                    onCreated={(c) => setCrews((prev) => [c, ...prev])}
                />
            )}
        </div>
    );
}
